import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { usePortalApi } from "./portal-api";
import { portalServer } from "../config";
import {
  useSyncData,
  useSyncModuleRegistration,
  type SyncModule,
} from "../sync";
import type { Information } from "../types";
import { limiter } from "./demo";
import { daily } from "./provider";
import { precacheResources } from "./resource";

const syncModuleId = "informationen";
const precacheSyncModuleId = `${syncModuleId}:precache`;

function useInformationenSyncModule(): SyncModule<ReadonlyArray<Information>> {
  const { portalKey, demo } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `tasks:${syncModuleId}`,
      defaultOptions: {
        schedule: { type: "periodic", interval: daily },
      },
      fetchData:
        useNewApi && baseUrl
          ? () => fetchInformationenNew(portalKey, baseUrl)
          : async () => {
              const response = await fetch(
                `${portalServer}/get-informations.php?ORG_ACCESSKEY=${encodeURIComponent(
                  portalKey,
                )}`,
              );

              if (!response.ok) {
                throw new Error(
                  `Error fetching data: ${response.status} ${response.statusText}`,
                );
              }

              const data = await response.json();
              const limited = limiter<Information>(demo, 3);
              return limited(data.INFORMATION_LIST ?? []).map(
                parseInformationLegacy,
              );
            },
    }),
    [demo, portalKey, useNewApi, baseUrl],
  );
}

async function fetchInformationenNew(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<Information>> {
  const response = await fetch(
    `${baseUrl}/api/device/sync/informations?includeArchived=false`,
    {
      headers: { Authorization: `Bearer ${portalKey}` },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return (data.information ?? []).map((i: any) =>
    parseInformationNew(i, portalKey, baseUrl),
  );
}

function parseInformationLegacy(i: any): Information {
  return {
    id: i.INFORMATION_ID,
    text: i.INFORMATION_TEXT,
    von: i.INFORMATION_VALID_FROM,
    bis: i.INFORMATION_VALID_TO,
  };
}

function parseInformationNew(
  i: any,
  portalKey: string,
  baseUrl: string,
): Information {
  return {
    id: i.id,
    text: i.description,
    von: i.dateFrom ?? "",
    bis: i.dateTo ?? "",
    datei:
      i.fileUrl && i.fileName
        ? {
            id: i.id,
            name: i.fileName,
            mimeType: i.fileMimeType ?? "application/octet-stream",
            url: `${baseUrl}/api/device/sync/informations/${encodeURIComponent(i.id)}/file/download`,
            fetchOptions: { headers: { Authorization: `Bearer ${portalKey}` } },
          }
        : undefined,
  };
}

export function useInformationenSync() {
  const module = useInformationenSyncModule();
  useSyncModuleRegistration(module);
  const precacheModule = useInformationenPrecacheSyncModule();
  useSyncModuleRegistration(precacheModule);
}

// Attached files are downloaded ahead of time so they can be opened offline.
function useInformationenPrecacheSyncModule(): SyncModule<void> {
  return useMemo(
    () => ({
      id: precacheSyncModuleId,
      i18nKey: `tasks:${syncModuleId}.precache`,
      defaultOptions: { schedule: { type: "periodic", interval: daily } },
      dependsOn: [syncModuleId],
      fetchData: async ({ dataProvider, observer }) => {
        const dateien = (
          dataProvider.getModuleData<ReadonlyArray<Information>>(
            syncModuleId,
          ) ?? []
        )
          .flatMap((i) => (i.datei ? [i.datei] : []))
          // Only what the app can display (PDF, images) — Word/Excel are opened in the portal.
          .filter(
            (d) =>
              d.mimeType === "application/pdf" ||
              d.mimeType.startsWith("image/"),
          );
        if (dateien.length === 0) return;

        await precacheResources(
          dateien.map((d) => d.url),
          { requestOptions: dateien[0].fetchOptions },
          observer,
          { skipFailures: true },
        );
      },
    }),
    [],
  );
}

export function useInformationen(): ReadonlyArray<Information> {
  return useSyncData(syncModuleId) ?? [];
}
